/**
 * Feature access resolution for the SPA — server-first, catalog fallback.
 *
 * `GET /api/features/access` (apps/api, `routes/features-access.ts`) is the
 * authority: it resolves access against the `plan_features` DB rows the admin
 * panel writes (PUT /api/admin/features), the owner's real identity (provider
 * profile, facility staff membership, claimed business — not just their own
 * subscriptions), current-period usage counters and any purchased batch packs.
 * None of that is visible to the browser, so anything resolved here alone is a
 * guess.
 *
 * We therefore ask the server for the whole scope in one batch and only fall
 * back to local computation when that call fails (offline, or a 404/403 window
 * during a rolling deploy):
 *
 *   1. `/api/features/access?scope=&ownerId=&keys=` → authoritative map
 *   2. fallback: `/api/account/billing` → active Subscription rows + plan codes
 *      + `PLAN_FEATURE_DEFAULTS` (catalog) → access map per plan code
 *      + `/api/account/billing/usage?scope=&entityId=` → metered counters
 *
 * `ResolvedFeatureAccess` is identical on both paths, so consumers never care
 * which one produced their entry. `resolveFeature` stays pure/sync for the
 * pages that already resolve a single key from a known plan code.
 */

import { ApiError, apiFetch } from "@pontimed/api-client";

import { FEATURE_BY_KEY, FEATURES, PLAN_FEATURE_DEFAULTS } from "./catalog";
import { computeUpgradeTarget } from "./hierarchy";
import type {
  FeatureDefinition,
  OwnerType,
  PlanFeatureAccess,
  ResolvedFeatureAccess,
} from "./types";

// ─── /api/account/billing response shape (mirrors apps/api route) ─────────

interface BillingSubscription {
  id: string;
  entityType: string;
  entityId: string;
  planTier: string | null;
  status: string;
  plan: { code: string; name: unknown; features: unknown } | null;
}

interface BillingResponse {
  subscriptions: BillingSubscription[];
  payments: unknown[];
  wallet: { balance: number; payFromWallet: boolean };
  hasStripeCustomer: boolean;
}

interface UsageResponse {
  period: { start: string; end: string };
  metered: Array<{
    feature: string;
    /** Sum across pools — NOT comparable to a single plan's limit on a
     *  context-scoped key; use `byContext` for that. */
    used: number;
    included: number | null;
    overage: number;
    unitPriceMinor: number;
    /** Per-pool split: '' = the owner's own pool, a facility id = that clinic's. */
    byContext?: Array<{ contextId: string; used: number }>;
  }>;
  totalOverageMinor: number;
}

/**
 * Per-(scope, owner) gating payload produced by `fetchUsage` and consumed by
 * `resolveBatch`. Exactly one of the two halves is populated per fetch:
 *
 *   - `access` — the server-resolved map (happy path). Already carries usage +
 *     exhausted per key, so metered counters aren't fetched at all.
 *   - the metered `UsageResponse` fields — fallback when the server gate is
 *     unreachable, so local resolution can still flag exhausted quotas.
 *
 * A plain `UsageResponse` is assignable to it, which keeps the old contract
 * valid for any caller written against the previous signature.
 */
export interface ScopeGatingData {
  /** Server-resolved access keyed by feature key. */
  access?: Record<string, ResolvedFeatureAccess>;
  period?: UsageResponse["period"];
  metered?: UsageResponse["metered"];
  totalOverageMinor?: number;
}

// ─── Plan resolution ──────────────────────────────────────────────────────

const FALLBACK_PLAN: Record<OwnerType, string> = {
  user: "user_free",
  provider: "provider_free",
  facility: "facility_starter",
  business: "business_free",
};

function entityTypeCandidates(scope: OwnerType): string[] {
  // Subscription.entityType uses legacy names for some scopes
  // (`patient` → catalog `user`).
  if (scope === "user") return ["user", "patient"];
  return [scope];
}

/**
 * Pick the active plan code for a given (scope, ownerId) from the billing
 * response. Mirrors `getOwnerPlanCode` from legacy gate.ts but operates on
 * the data the client already has.
 */
export function pickPlanCode(
  billing: BillingResponse | undefined,
  scope: OwnerType,
  ownerId: string | null,
): string | null {
  if (!billing) return null;
  const candidateTypes = entityTypeCandidates(scope);
  const matches = billing.subscriptions
    .filter((s) => candidateTypes.includes(s.entityType))
    .filter((s) => (ownerId ? s.entityId === ownerId : true))
    .filter((s) => s.status === "active");

  // Prefer exact entityId match; fall back to any active sub for the scope.
  const exact = matches.find((s) => s.entityId === ownerId);
  const chosen = exact ?? matches[0] ?? null;
  if (!chosen) {
    // user-scope auto-promote: any active paid sub on this user → user_plus.
    if (scope === "user") {
      const anyActive = billing.subscriptions.some((s) => s.status === "active");
      if (anyActive) return "user_plus";
    }
    return null;
  }
  return chosen.plan?.code ?? null;
}

// ─── Feature access resolution (pure) ─────────────────────────────────────

const MISSING: PlanFeatureAccess = { access: "hidden" };

/**
 * Keys whose quota is counted per workspace (Wave 2a / §11.7a). Source of truth
 * is `CONTEXT_SCOPED_FEATURE_KEYS` in apps/api/src/lib/features/gate.ts; that
 * module is server-only, so the list is mirrored here for the OFFLINE fallback
 * and nowhere else — the happy path takes the server's answer verbatim.
 */
const CONTEXT_SCOPED_FEATURE_KEYS = new Set<string>(["provider.bookings.per_month"]);

/**
 * Usage to compare against the plan we resolved locally.
 *
 * For a context-scoped key, `metered[].used` is the SUM over every pool — the
 * doctor's own plus each clinic that covers him. Comparing that sum against one
 * plan's limit marked a doctor "exhausted" on his own 10 bookings because a
 * clinic had spent 40 out of its 100. The own pool is the row with contextId ''
 * and it is the only one that answers for the plan code we picked here; no such
 * row means nothing was spent from it.
 */
function localUsageFor(entry: UsageResponse["metered"][number]): number {
  if (!CONTEXT_SCOPED_FEATURE_KEYS.has(entry.feature)) return entry.used;
  const ownPool = entry.byContext?.find((c) => c.contextId === "");
  return ownPool?.used ?? 0;
}

/**
 * Resolve a single feature for a given plan code. When the plan has no entry
 * for the feature, falls back to the catalog default, then to "hidden".
 *
 * `usage` is optional — pass the current period count so quota exhaustion can
 * be flagged. When omitted we assume usage = 0.
 *
 * Catalog-only: DB overrides written by the admin panel are NOT visible here.
 * Prefer the server-resolved entries from `resolveBatch` where you have them.
 */
export function resolveFeature(
  planCode: string | null,
  featureKey: string,
  usage: number | null = null,
): ResolvedFeatureAccess {
  const definition: FeatureDefinition | undefined = FEATURE_BY_KEY[featureKey];
  if (!definition) {
    // Unknown feature key — treat as hidden so consumers don't accidentally
    // unlock unmapped features. Caller can still surface the raw key.
    return {
      access: "hidden",
      definition: {
        key: featureKey,
        scope: "user",
        category: "unknown",
        labelRo: featureKey,
        labelEn: featureKey,
        type: "bool",
        status: "idea",
      },
    };
  }

  const planMap = planCode ? PLAN_FEATURE_DEFAULTS[planCode] : undefined;
  const plan = planMap?.[featureKey] ?? MISSING;

  let upgradeTo = plan.upgradeTo;
  if (
    !upgradeTo &&
    planCode &&
    (plan.access === "upgrade" ||
      plan.access === "hidden" ||
      plan.access === "teaser" ||
      plan.access === "soon")
  ) {
    upgradeTo = computeUpgradeTarget(planCode, featureKey) ?? undefined;
  }

  let exhausted = false;
  if (
    plan.access === "limited" &&
    typeof plan.limit === "number" &&
    plan.limit !== -1 &&
    typeof usage === "number"
  ) {
    exhausted = usage >= plan.limit;
  }

  return {
    access: plan.access,
    limit: plan.limit,
    upgradeTo,
    definition,
    usage,
    exhausted,
  };
}

/**
 * Resolve a batch of features for a (scope, ownerId). Pure — no fetches.
 *
 * Server-resolved entries (from `fetchUsage`) win; keys the server didn't
 * return fall back to catalog defaults + locally available billing/usage data.
 */
export function resolveBatch(
  billing: BillingResponse | undefined,
  gating: ScopeGatingData | undefined,
  scope: OwnerType,
  ownerId: string | null,
  keys: string[],
): Record<string, ResolvedFeatureAccess> {
  const serverAccess = gating?.access;
  const planCode = pickPlanCode(billing, scope, ownerId) ?? FALLBACK_PLAN[scope];
  const usageByKey = new Map<string, number>(
    (gating?.metered ?? []).map((m) => [m.feature, localUsageFor(m)]),
  );
  const out: Record<string, ResolvedFeatureAccess> = {};
  for (const key of keys) {
    out[key] = serverAccess?.[key] ?? resolveFeature(planCode, key, usageByKey.get(key) ?? null);
  }
  return out;
}

// ─── Network helpers (used by the provider via TanStack Query) ────────────

export async function fetchBilling(): Promise<BillingResponse | null> {
  try {
    return await apiFetch.get<BillingResponse>("/api/account/billing");
  } catch (err) {
    // Unauthenticated callers (signed-out, or auth layer not yet wired) get
    // a 401 — return null so resolveBatch falls back to FREE plan defaults.
    if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
      return null;
    }
    throw err;
  }
}

/** Every catalog key of a scope, so one batch covers whatever the caller asks. */
const CATALOG_KEYS_BY_SCOPE: Record<OwnerType, string[]> = (() => {
  const out: Record<OwnerType, string[]> = {
    user: [],
    provider: [],
    facility: [],
    business: [],
  };
  for (const feature of FEATURES) {
    out[feature.scope].push(feature.key);
  }
  return out;
})();

// Keeps the query string well under any proxy limit (~40 chars/key). The
// biggest scope today is 77 keys → one request; growth adds a second.
const MAX_KEYS_PER_REQUEST = 80;

/**
 * Server-resolved access for a batch of feature keys — the authoritative path
 * (DB plan overrides, real owner identity, usage + batch packs).
 *
 * Returns `null` on ANY failure (offline, 401/403/404, 5xx) so callers can
 * degrade to catalog defaults instead of surfacing a query error.
 */
export async function fetchFeatureAccessBatch(
  scope: OwnerType,
  ownerId: string | null,
  keys: string[],
  facilityId?: string | null,
): Promise<Record<string, ResolvedFeatureAccess> | null> {
  if (keys.length === 0) return {};
  try {
    const chunks: string[][] = [];
    for (let i = 0; i < keys.length; i += MAX_KEYS_PER_REQUEST) {
      chunks.push(keys.slice(i, i + MAX_KEYS_PER_REQUEST));
    }
    const parts = await Promise.all(
      chunks.map((chunk) => requestAccess(scope, ownerId, chunk, facilityId)),
    );
    const merged: Record<string, ResolvedFeatureAccess> = {};
    for (const part of parts) Object.assign(merged, part);
    return merged;
  } catch {
    return null;
  }
}

async function requestAccess(
  scope: OwnerType,
  ownerId: string | null,
  keys: string[],
  facilityId?: string | null,
): Promise<Record<string, ResolvedFeatureAccess>> {
  const params = new URLSearchParams({ scope });
  if (ownerId) params.set("ownerId", ownerId);
  // The workspace the answer is for (Wave 2a / §11.7a): a doctor covered by a
  // clinic spends that clinic's booking pool while he works there, so the same
  // key legitimately answers differently per facility. Only screens that KNOW
  // which clinic they are in pass it; everywhere else the server resolves the
  // doctor's own pool, exactly as before.
  if (facilityId) params.set("facilityId", facilityId);
  // The endpoint answers a single object for `key` and a map for `keys`.
  if (keys.length === 1) params.set("key", keys[0]);
  else params.set("keys", keys.join(","));

  const data = await apiFetch.get<Record<string, ResolvedFeatureAccess> | ResolvedFeatureAccess>(
    `/api/features/access?${params.toString()}`,
  );
  return keys.length === 1
    ? { [keys[0]]: data as ResolvedFeatureAccess }
    : (data as Record<string, ResolvedFeatureAccess>);
}

/**
 * Gating payload for one (scope, owner): the server-resolved access map, or —
 * when that endpoint is unreachable — the metered usage counters the local
 * fallback needs. Cached per (scope, ownerId) by the caller.
 */
export async function fetchUsage(
  scope: OwnerType,
  entityId: string,
  facilityId?: string | null,
): Promise<ScopeGatingData | null> {
  const access = await fetchFeatureAccessBatch(
    scope,
    entityId,
    CATALOG_KEYS_BY_SCOPE[scope] ?? [],
    facilityId,
  );
  // Server map already carries usage + exhausted per key — no counters needed.
  if (access) return { access };

  try {
    return await apiFetch.get<UsageResponse>(
      `/api/account/billing/usage?scope=${encodeURIComponent(scope)}&entityId=${encodeURIComponent(entityId)}`,
    );
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 401 || err.status === 403 || err.status === 404)
    ) {
      return null;
    }
    throw err;
  }
}

// Re-exports for ergonomic single-import surface from consumers.
export { FEATURE_BY_KEY, PLAN_FEATURE_DEFAULTS } from "./catalog";
export { computeUpgradeTarget, PLAN_HIERARCHY } from "./hierarchy";
export { findRouteGate, ROUTE_GATES } from "./routes";
export type {
  FeatureAccess,
  FeatureDefinition,
  FeatureScope,
  FeatureStatus,
  FeatureType,
  OwnerType,
  PlanFeatureAccess,
  ResolvedFeatureAccess,
} from "./types";
